import React from 'react';
import { graphql } from 'gatsby';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import { RichTextEl } from '../Global/RichText';
//@ts-ignore
import * as styles from './ContentIllustration.module.css';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { isRichTextFieldBlank } from '../../global/utility';
import {
    KontentStringValue,
    KontentRichText,
    KontentMedia,
    BrandColor,
    CTALocation,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';

interface Props {
    kontentItemId?: string;
    sub_headline: KontentRichText;
    headline: KontentRichText;
    description: KontentRichText;
    desktop_image: KontentMedia;
    mobile_image: KontentMedia;
    bottom_copy: KontentRichText;
    bottom_copy_logo: KontentMedia;
    cta_configuration__cta_label: KontentStringValue;
    cta_configuration__cta_location: CTALocation;
    cta_configuration__cta_location_anchor_point: KontentStringValue;
    cta_configuration__cta_window_action: KontentRadioMultiChoice<
        'same_window' | 'new_window'
    >;
    background_color: BrandColor;
    font_color: BrandColor;
    anchor: KontentStringValue;
}

const ContentIllustration: React.FC<Props> = ({
    sub_headline,
    headline,
    description,
    desktop_image,
    mobile_image,
    bottom_copy,
    bottom_copy_logo,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_window_action,
    background_color,
    font_color,
    anchor,
    kontentItemId,
}) => {
    const breakpoints = useBreakpoint();
    const backgroundColor = getColor(background_color) || '';
    const textColor = getColor(font_color) || '';
    const desktopImg = getMediaData(desktop_image);
    const mobileImg = getMediaData(mobile_image);
    const bottomLogo = getMediaData(bottom_copy_logo);

    const ctaHref = BuildLinkHref(
        cta_configuration__cta_location,
        cta_configuration__cta_location_anchor_point
    );
    const ctaTarget =
        getMultiChoice(cta_configuration__cta_window_action) === 'new_window'
            ? '_blank'
            : undefined;
    const showCta =
        cta_configuration__cta_label?.value &&
        cta_configuration__cta_location?.value;

    return (
        <section
            className={styles.wrapper}
            id={anchor?.value || undefined}
            style={{ backgroundColor, color: textColor }}
            data-kontent-item-id={kontentItemId}
        >
            <div className={styles.container}>
                <div className={styles.copyColumn}>
                    {!isRichTextFieldBlank(sub_headline?.value) && (
                        <div className={styles.subHeadline}>
                            {RichText(
                                sub_headline,
                                false,
                                undefined,
                                undefined,
                                'sub_headline'
                            )}
                        </div>
                    )}

                    {!isRichTextFieldBlank(headline?.value) && (
                        <div className={styles.headline}>
                            {RichText(
                                headline,
                                false,
                                undefined,
                                undefined,
                                'headline'
                            )}
                        </div>
                    )}

                    {!isRichTextFieldBlank(description?.value) && (
                        <div className={styles.description}>
                            {RichText(
                                description,
                                false,
                                undefined,
                                undefined,
                                'description'
                            )}
                        </div>
                    )}
                </div>

                <div
                    className={styles.imageColumn}
                    data-kontent-element-codename="desktop_image"
                >
                    {breakpoints.md
                        ? desktopImg?.url && (
                              <img
                                  src={optimizeKontentImageUrl(desktopImg.url, {
                                      ...imagePreset.halfWidth.desktop,
                                      q: 95,
                                  })}
                                  srcSet={buildKontentSrcSet(
                                      desktopImg.url,
                                      imagePreset.halfWidth.desktop.w,
                                      { fm: 'webp' as const, q: 95 }
                                  )}
                                  sizes="(max-width: 1100px) 100vw, 1100px"
                                  alt={
                                      desktopImg.description || desktopImg.name
                                  }
                                  className={styles.image}
                                  loading="lazy"
                              />
                          )
                        : mobileImg?.url && (
                              <img
                                  src={optimizeKontentImageUrl(mobileImg.url, {
                                      ...imagePreset.fullWidth.mobile,
                                      q: 95,
                                  })}
                                  srcSet={buildKontentSrcSet(
                                      mobileImg.url,
                                      imagePreset.fullWidth.mobile.w,
                                      {
                                          fm: 'webp' as const,
                                          q: 95,
                                      }
                                  )}
                                  sizes="100vw"
                                  alt={mobileImg.description || mobileImg.name}
                                  className={styles.image}
                                  loading="lazy"
                              />
                          )}
                </div>

                <div className={styles.bottomCopyRow}>
                    {!isRichTextFieldBlank(bottom_copy?.value) && (
                        <RichTextEl
                            richText={bottom_copy}
                            wrapperClassName={styles.bottomCopy}
                            elementCodename="bottom_copy"
                        />
                    )}
                    {bottomLogo?.url && (
                        <img
                            src={optimizeKontentImageUrl(bottomLogo.url, {
                                w: 400,
                                fm: 'webp' as const,
                                q: 95,
                            })}
                            srcSet={buildKontentSrcSet(bottomLogo.url, 400, {
                                fm: 'webp' as const,
                                q: 95,
                            })}
                            sizes="200px"
                            alt={bottomLogo.description || bottomLogo.name}
                            className={styles.bottomLogo}
                            loading="lazy"
                        />
                    )}
                </div>

                {showCta ? (
                    <div className={styles.ctaContainer}>
                        <CtaLink
                            href={ctaHref}
                            target={ctaTarget}
                            visualStyle="outlineLight"
                            elementCodename="cta_configuration__cta_label"
                        >
                            {cta_configuration__cta_label.value}
                        </CtaLink>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default ContentIllustration;

export const fragmentContentIllustration = graphql`
    fragment ContentIllustration on kontent_item_component___content_illustration {
        system {
            id
            type
        }
        elements {
            sub_headline {
                ...kontentRichText
            }
            headline {
                ...kontentRichText
            }
            description {
                ...kontentRichText
            }
            desktop_image {
                ...kontentImage
            }
            mobile_image {
                ...kontentImage
            }
            bottom_copy {
                ...kontentRichText
            }
            bottom_copy_logo {
                ...kontentImage
            }
            background_color {
                ...kontentColor
            }
            font_color {
                ...kontentColor
            }
            anchor {
                value
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
        }
    }
`;
